import React from "react";
import { Box, Typography } from "@mui/material";
import { tokens } from "../../theme";

const STEPS = [
  { id: 1, label: "Cliente" },
  { id: 2, label: "Trabajo y fotos" },
  { id: 3, label: "Firma" },
];

const CheckMark = ({ size = 11 }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="3.4"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M5 12.5l4.5 4.5L19 7" />
  </svg>
);

/**
 * Encabezado de pasos.
 *   · Teléfono: tres barras con su etiqueta, que es lo que entra.
 *   · Escritorio: tres tarjetas con el estado de cada paso debajo del nombre.
 * Se puede volver a un paso cerrado y avanzar al siguiente; si falta un dato,
 * `onStepClick` lo explica en vez de dejar el click muerto.
 */
const StepHeader = ({
  currentStep,
  onStepClick,
  canAdvance = false,
  subtitleFor,
  title = "Nueva orden de servicio",
  subtitle,
}) => {
  const stepState = (id) => {
    if (id < currentStep) return "done";
    if (id === currentStep) return "active";
    return "pending";
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: "14px", width: "100%" }}>
      {/* En escritorio el título vive en la barra superior; acá sería repetirlo. */}
      <Box sx={{ display: { xs: "flex", md: "none" }, flexDirection: "column", gap: "2px" }}>
        <Typography sx={{ fontSize: "17px", fontWeight: 600, letterSpacing: "-0.015em" }}>
          {title}
        </Typography>
        <Typography sx={{ fontSize: "12.5px", color: tokens.ink3 }}>
          {subtitle || `Paso ${currentStep} de ${STEPS.length}`}
        </Typography>
      </Box>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
          gap: { xs: "8px", md: "14px" },
        }}
      >
        {STEPS.map((step) => {
          const state = stepState(step.id);
          const isNext = step.id === currentStep + 1;
          const clickable = typeof onStepClick === "function" && (state === "done" || isNext);
          const pending = isNext && !canAdvance;

          const accent =
            state === "done" ? "#6fa32e" : state === "active" ? tokens.brand : tokens.border;

          return (
            <Box
              key={step.id}
              onClick={clickable ? () => onStepClick(step.id) : undefined}
              role={clickable ? "button" : undefined}
              aria-current={state === "active" ? "step" : undefined}
              aria-label={clickable ? `Ir al paso ${step.id}: ${step.label}` : undefined}
              title={clickable ? `Ir a ${step.label}` : undefined}
              sx={{
                display: "flex",
                flexDirection: { xs: "column", md: "row" },
                alignItems: { md: "center" },
                gap: { xs: "7px", md: "10px" },
                minWidth: 0,
                cursor: clickable ? "pointer" : "default",
                opacity: pending ? 0.75 : 1,
                // Teléfono: barra + etiqueta. Escritorio: tarjeta.
                paddingY: { xs: "6px", md: "13px" },
                paddingX: { xs: 0, md: "16px" },
                borderRadius: { xs: "8px", md: `${tokens.radiusCard}px` },
                backgroundColor: { md: tokens.surface },
                border: { xs: "none", md: `1px solid ${state === "active" ? tokens.brand : tokens.border}` },
                boxShadow: { md: state === "active" ? `0 0 0 3px ${tokens.brandTint}` : "none" },
                transition: "border-color 120ms ease, box-shadow 120ms ease",
                "&:hover": clickable
                  ? { backgroundColor: { xs: tokens.borderSoft, md: tokens.surface }, borderColor: { md: tokens.borderStrong } }
                  : undefined,
              }}
            >
              <Box
                sx={{
                  display: { xs: "block", md: "none" },
                  height: 3,
                  borderRadius: "2px",
                  backgroundColor: accent,
                }}
              />

              <Box sx={{ display: "flex", alignItems: "center", gap: { xs: "6px", md: "10px" }, minWidth: 0 }}>
                <Box
                  sx={{
                    width: { xs: 16, md: 22 },
                    height: { xs: 16, md: 22 },
                    flexShrink: 0,
                    borderRadius: 999,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: { xs: "10px", md: "11.5px" },
                    fontWeight: 600,
                    fontVariantNumeric: "tabular-nums",
                    backgroundColor:
                      state === "done" ? "#6fa32e" : state === "active" ? tokens.brand : "transparent",
                    color: state === "pending" ? tokens.ink3 : "#fff",
                    border: state === "pending" ? `1px solid ${tokens.borderStrong}` : "none",
                  }}
                >
                  {state === "done" ? <CheckMark /> : step.id}
                </Box>

                <Box sx={{ display: "flex", flexDirection: "column", gap: "1px", minWidth: 0 }}>
                  <Typography
                    sx={{
                      fontSize: { xs: "12px", md: "13.5px" },
                      fontWeight: state === "active" ? 600 : 500,
                      color:
                        state === "done"
                          ? "#3f6b1f"
                          : state === "active"
                          ? tokens.ink
                          : tokens.ink3,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {step.label}
                  </Typography>

                  {/* El subtítulo sólo cabe en escritorio. */}
                  <Typography
                    sx={{
                      display: { xs: "none", md: "block" },
                      fontSize: "11.5px",
                      color: state === "pending" ? tokens.ink4 : tokens.ink3,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {(subtitleFor && subtitleFor(step.id, state)) ||
                      (state === "done" ? "Listo" : state === "active" ? "En edición" : "Pendiente")}
                  </Typography>
                </Box>
              </Box>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};

export default StepHeader;
